/**
 * User feature flags for platform access control
 */
export interface UserFeatureFlags {
  // Role-based access
  approvedRetailer?: boolean;
  approvedBuyer?: boolean;
  approvedDistributor?: boolean;
  tradePartner?: boolean;
  // Feature access
  canCreateBrands?: boolean;
  canManageProducts?: boolean;
  canCreateInitiatives?: boolean;
  canAccessAnalytics?: boolean;
  canManageQuizzes?: boolean;

  // Beta features
  betaFeatures?: boolean;
  advancedAnalytics?: boolean;

  // Add more flags here as needed
  [key: string]: boolean | number | string | undefined;
}

/**
 * Feature flag keys for type-safe access
 */
export type FeatureFlagKey = keyof UserFeatureFlags;

/**
 * Feature flag value types
 */
export type FeatureFlagValueType = 'boolean' | 'number' | 'string';

/**
 * Feature flag category for grouping
 */
export type FeatureFlagCategory = 'roles' | 'features' | 'beta' | 'other';

/**
 * Feature flag definition with metadata
 */
export interface FeatureFlagDefinition {
  key: string;
  label: string;
  description: string;
  type: FeatureFlagValueType;
  category: FeatureFlagCategory;
  defaultValue: boolean | number | string;
  min?: number; // For number types
  max?: number; // For number types
}

/**
 * All feature flag definitions
 */
export const FEATURE_FLAG_DEFINITIONS: FeatureFlagDefinition[] = [
  // Role-based access
  {
    key: 'approvedRetailer',
    label: 'Approved Retailer',
    description: 'Access to retailer-specific features and dashboards',
    type: 'boolean',
    category: 'roles',
    defaultValue: false,
  },
  {
    key: 'approvedBuyer',
    label: 'Approved Buyer',
    description: 'Access to buyer-specific features and ordering',
    type: 'boolean',
    category: 'roles',
    defaultValue: false,
  },
  {
    key: 'approvedDistributor',
    label: 'Approved Distributor',
    description: 'Access to distributor-specific features and logistics',
    type: 'boolean',
    category: 'roles',
    defaultValue: false,
  },

  // Feature access
  {
    key: 'canCreateBrands',
    label: 'Can Create Brands',
    description: 'Permission to create and manage brand entities',
    type: 'boolean',
    category: 'features',
    defaultValue: false,
  },
  {
    key: 'canManageProducts',
    label: 'Can Manage Products',
    description: 'Permission to add, edit, and delete products',
    type: 'boolean',
    category: 'features',
    defaultValue: false,
  },
  {
    key: 'canCreateInitiatives',
    label: 'Can Create Initiatives',
    description: 'Permission to create campaigns and initiatives',
    type: 'boolean',
    category: 'features',
    defaultValue: false,
  },
  {
    key: 'canAccessAnalytics',
    label: 'Can Access Analytics',
    description: 'Permission to view analytics dashboards',
    type: 'boolean',
    category: 'features',
    defaultValue: false,
  },
  {
    key: 'canManageQuizzes',
    label: 'Can Manage Quizzes',
    description: 'Permission to create and manage quizzes',
    type: 'boolean',
    category: 'features',
    defaultValue: false,
  },

  // Beta features
  {
    key: 'betaFeatures',
    label: 'Beta Features',
    description: 'Access to experimental beta features',
    type: 'boolean',
    category: 'beta',
    defaultValue: false,
  },
  {
    key: 'advancedAnalytics',
    label: 'Advanced Analytics',
    description: 'Access to advanced analytics and insights',
    type: 'boolean',
    category: 'beta',
    defaultValue: false,
  },
  {
    key: 'tradePartner',
    label: 'Trade Partner',
    description: 'Access to Trade Hub and B2B features',
    type: 'boolean',
    category: 'roles',
    defaultValue: false,
  },
];

/**
 * Default feature flags for new users
 */
export const DEFAULT_FEATURE_FLAGS: UserFeatureFlags = FEATURE_FLAG_DEFINITIONS.reduce(
  (acc, def) => {
    acc[def.key] = def.defaultValue;
    return acc;
  },
  {} as UserFeatureFlags
);

/**
 * Get feature flag definition by key
 */
export function getFeatureFlagDefinition(key: string): FeatureFlagDefinition | undefined {
  return FEATURE_FLAG_DEFINITIONS.find((def) => def.key === key);
}

/**
 * Get feature flags grouped by category
 */
export function getFeatureFlagsByCategory(): Record<FeatureFlagCategory, FeatureFlagDefinition[]> {
  return FEATURE_FLAG_DEFINITIONS.reduce(
    (acc, def) => {
      if (!acc[def.category]) {
        acc[def.category] = [];
      }
      acc[def.category].push(def);
      return acc;
    },
    {} as Record<FeatureFlagCategory, FeatureFlagDefinition[]>
  );
}
